import store from "@/store";
import { removeItem } from "@/utils/va-order-test/testAndPanelSelection";

export function getPricesAfterCouponApplied(couponDetails, orderedItemsPrice, unlockDeals) {
  let { priceDetail, totalDeduction } = couponDetails;
  // calculate price after coupon applied single tests without breakdown ordered bundles
  let deductionWithoutBreakdown = priceDetail.filter(item => !item.bundleId)
    .reduce((acc, item) => acc + (item.originalPrice - item.sellingPrice), 0);
  let priceWithoutBreakdown = Number((orderedItemsPrice - deductionWithoutBreakdown).toFixed(2));

  // calculate price after coupon applied with breakdown bundles AND single tests without breakdown
  let breakDownBundleIds = priceDetail.map(item => item.bundleId);
  let breakDwonBundleDeduction = unlockDeals.filter(deal => breakDownBundleIds.includes(deal.deal_id))
    .reduce((acc, deal) => acc + Number(deal.deduction), 0);
  let priceWithBreakdown = Number((orderedItemsPrice + breakDwonBundleDeduction - Number(totalDeduction)).toFixed(2));
  return {
    priceWithBreakdown,
    priceWithoutBreakdown,
  };
}

export function breakdownCouponEligibleBundles(priceDetails, unlockDeals, supportObj) {
  if (!unlockDeals) return;
  let { selectedItemList, testNodeList } = supportObj;
  let selectedShortcuts = selectedItemList.filter(item => item.category === "shortcut");
  let breakDownBundleIds = priceDetails.map(item => item.bundleId);
  unlockDeals.filter(deal => breakDownBundleIds.includes(deal.deal_id)).forEach(deal => {
    let bundle = selectedItemList.find(item => ["discountPanel", "xChooseY"].includes(item.category) && item.bundleId.toString() === deal.deal_id);
    if (bundle) { // found selected bundles that need to be broken down to apply coupon
      bundle.category === "discountPanel" 
        ? store.dispatch("orderTest/removeSelectedDiscountPanel", bundle) 
        : store.dispatch("orderTest/removeSelectedMultiChoosePanel", bundle);
      deal.involved_item_ids.forEach(id => {
        let testNode = testNodeList.find(node => node.id.toString() === id);
        if (testNode) {
          store.dispatch("orderTest/addSelectedTest", testNode.value);
        }
      });
    } else { 
      // check if any shortcuts include bundles that need to be broken down to apply coupon
      selectedShortcuts.filter(shortcut => {
        let { testIdList, discountPanelIdList, xChooseYIdList, selectedXChooseYPanelList } = shortcut;
        let discountPanelNeedBreakdown = discountPanelIdList.find(id => id.toString() === deal.deal_id);
        let xChooseYNeedBreakdown = xChooseYIdList.find(xChooseY => xChooseY.bundleId.toString() === deal.deal_id);
        if (discountPanelNeedBreakdown) {
          removeItem(Number(deal.deal_id), discountPanelIdList);
          deal.involved_item_ids.forEach(id => {
            testIdList.push(id.includes("_") ? id : Number(id));
          });
        }
        if (xChooseYNeedBreakdown) {
          // note that need to remove from xChooseYIdList and selectedXChooseYPanelList
          removeItem(xChooseYNeedBreakdown, xChooseYIdList);
          let xChooseY = selectedXChooseYPanelList.find(xChooseY => xChooseY.bundleId.toString() === deal.deal_id);
          removeItem(xChooseY, selectedXChooseYPanelList);
          deal.involved_item_ids.forEach(id => {
            testIdList.push(id.includes("_") ? id : Number(id));
          });
        }
        if (discountPanelNeedBreakdown || xChooseYNeedBreakdown) {
          // update shortcut's price
          shortcut.price += Number(deal.deduction);
        }
      });
    }
  });
}

export function handleShortcutAfterCouponRemoved(selectedShortcuts, unlockDeals) {
  if (selectedShortcuts.length === 0 || !unlockDeals) return;
  unlockDeals.forEach(deal => {
    let bundle = store.getters["orderTest/panels/bundleList"].find(bundle => bundle.bundleId.toString() === deal.deal_id);
    if (bundle) {
      selectedShortcuts.forEach(shortcut => {
        let { testIdList, discountPanelIdList, xChooseYIdList, selectedXChooseYPanelList } = shortcut;
        let dealTestIds = deal.involved_item_ids;
        if (dealTestIds.every(id => testIdList.map(i => i.toString()).includes(id.toString()))) {
          // recover shortcut's bundles if dealTestIds are all in shortcut's testIdList
          if (bundle.category === "discountPanel") {
            discountPanelIdList.push(bundle.bundleId);
          } else {
            xChooseYIdList.push({
              bundleId: Number(bundle.bundleId),
              selectedZoomerIdList: bundle.selectedZoomerIdList,
              selectedSupplementIdList: bundle.selectedSupplementIdList
            });
            selectedXChooseYPanelList.push(bundle);
          }
          dealTestIds.forEach(id => removeItem(id.includes("_") ? id : Number(id), testIdList));
          shortcut.price -= Number(deal.deduction);
        }
      })
    }
  })
}

export function getVerifyCouponApiPayload(sampleId, currency, selectedItemList, promoCode, supportObj, patientId = null) {
  let { testNodeList, bundleList, selectedSeasonalPromoList } = supportObj;
  let testList = testNodeList.map(node => node.value);
  let { testIds, promos, discountPanels, xChooseYPanels } = getCouponEligibleItems(selectedItemList, bundleList, selectedSeasonalPromoList);
  let data = {
    coupon_code: promoCode,
    item_ids: getTestIdsForCoupon(testIds, promos, discountPanels, xChooseYPanels),
    unlock_deals: getUnlockDeals(promos, discountPanels, xChooseYPanels, testList),
    currency: currency.toLowerCase(),
    sample_id: sampleId,
    patient_id: patientId
  };
  return data;
}

/**
 * get coupon eligible items
 * test ids with original price: va,vw test alone & tests in shortcuts
 * exclude promotion, discount panels, xChooseY panels & custom panels
 * *******************************************************************
 * 04/23/24 udpate: include tests has promotions as well
 * *******************************************************************
 * 05/01/24 update: include tests in discount panels (for coupon ECO)
 * *******************************************************************
 * 05/15/24 update: include tests in xChooseY panels
 * *******************************************************************
 * 05/13/25 update: exclude items with markup (VP-10603) when patient pay now or patient pay later
 * *******************************************************************
 * 08/18/25 update: include items with markup (VP-10603) when patient pay now or patient pay later
 */
function getCouponEligibleItems(selectedItemList, bundleList, selectedSeasonalPromoList) {
  let eligibleItems = selectedItemList
  let selectedShortcutList = eligibleItems.filter(item => item.category === "shortcut");
  // single tests ids & tests in shortcuts ids
  let selectedTestIds = eligibleItems.filter(item => ["va", "vw"].includes(item.category)).map(item => item.id);
  let shortcutTestIds = selectedShortcutList.reduce((acc, shortcut) => acc.concat(shortcut.testIdList), []);
  // discount panel & shortcut discount panel
  let shortcutDiscountPanelIds = selectedShortcutList.reduce((acc, shortcut) => acc.concat(shortcut.discountPanelIdList), []);
  let shortcutDiscountPanels = bundleList.filter(bundle => shortcutDiscountPanelIds.includes(bundle.bundleId));
  let discountPanels = eligibleItems.filter(item => item.category === "discountPanel").concat(shortcutDiscountPanels);
  // xChooseY panel & shortcut xChooseY panel
  let shortcutXChooseYPanels = selectedShortcutList.map(shortcut => shortcut.xChooseYIdList).flat()
    .map(xChooseY => {
      let panelPrice = bundleList.find(bundle => bundle.bundleId === xChooseY.bundleId).price;
      return {
        ...xChooseY,
        price: panelPrice
      }
    });
  let xChooseYPanels = eligibleItems.filter(item => item.category === "xChooseY").concat(shortcutXChooseYPanels);
  return {
    testIds: selectedTestIds.concat(shortcutTestIds),
    promos: selectedSeasonalPromoList,
    discountPanels,
    xChooseYPanels,
  }
}

/**
 * get all ordered test ids with original price for check coupon api
 */
function getTestIdsForCoupon(testIds, promos, discountPanels, xChooseYPanels) {
  let modifiedTestIds = testIds.map(id => {
      return typeof id === "string" && id.includes("_") 
        ? Number(id.split("_")[0]) : id
    });
  let promoTestIds = promos.reduce((acc, promo) => acc.concat([...promo.testIdsInPromo]), []);
  let discountPanelTestIds = discountPanels.reduce((acc, panel) => acc.concat(panel.testIdsInPanel), []);
  let xChooseYPanelTestIds = xChooseYPanels.reduce((acc, panel) => {
    let { selectedZoomerIdList, selectedSupplementIdList } = panel;
    let testIds = (selectedZoomerIdList ? selectedZoomerIdList : []).concat(selectedSupplementIdList ? selectedSupplementIdList : []);
    return acc.concat(testIds);
  }, []);
  let stringTestIds = modifiedTestIds.concat(promoTestIds).concat(discountPanelTestIds).concat(xChooseYPanelTestIds);
  return [...new Set(stringTestIds.map(id => id.toString()))];
}
/**
 * get promo deal info, used for verifyCoupon api
 * ==============================================
 * 05/01/update: add discount panel info
 * ==============================================
 * 05/15/25 update: add xChooseY panel info
 */
function getUnlockDeals(promos, discountPanels, xChooseYPanels, testList) {
  let unlockDeals = [];
  promos.forEach(promo => {
    unlockDeals.push({
      deal_id: promo.id.toString(),
      involved_item_ids: [...promo.testIdsInPromo].map(id => id.toString()),
      deduction: (promo.originalPrice - promo.price).toFixed(2)
    });
  });
  // discount panel info
  discountPanels.forEach(panel => {
    unlockDeals.push({
      deal_id: (panel.id || panel.bundleId).toString(),
      deal_type:"discount_panel",
      involved_item_ids: panel.testIdsInPanel.map(id => id.toString()),
      deduction: (panel.originalPrice - panel.price).toFixed(2)
    });
  });
  // xChooseY panel info
  xChooseYPanels.forEach(panel => {
    let { selectedZoomerIdList, selectedSupplementIdList } = panel;
    let testIds = (selectedZoomerIdList ? selectedZoomerIdList : []).concat(selectedSupplementIdList ? selectedSupplementIdList : []);
    let originalPrice = testList.filter(test => testIds.includes(test.id)).reduce((acc, test) => acc + test.price, 0);
    unlockDeals.push({
      deal_id: (panel.id || panel.bundleId).toString(),
      deal_type:"xChooseY_panel",
      involved_item_ids: testIds.map(id => id.toString()),
      deduction: (originalPrice - panel.price).toFixed(2)
    });
  });
  return unlockDeals;
}